// installationApp.js — savoir si l'application peut être installée sur
// l'appareil, et à quelles conditions on ose le proposer.
//
// Deux mondes, deux mécaniques :
//
//   Android / Chrome / Edge  Le navigateur émet `beforeinstallprompt` quand la
//                            page remplit ses critères (manifeste complet,
//                            icônes 192 et 512, service worker qui répond aux
//                            navigations). On l'intercepte, on le garde, et
//                            c'est NOTRE bouton qui déclenche l'installation.
//
//   iOS / iPadOS             Aucun événement, aucune API : l'ajout à l'écran
//                            d'accueil passe obligatoirement par le menu
//                            Partager. On ne peut donc que l'expliquer.
//
// Dans les deux cas, on ne s'adresse qu'à un TÉLÉPHONE OU UNE TABLETTE.
// `beforeinstallprompt` ne dit rien de l'appareil : Chrome et Edge de bureau
// l'émettent exactement comme Chrome Android. Or ce qu'on propose là — une
// icône sur l'écran d'accueil, un lancement en plein écran — n'a de sens qu'au
// bout d'un doigt : sur un ordinateur, l'application vit déjà dans un onglet,
// et le bandeau n'y serait qu'une bande de plus en haut de l'écran.
//
// L'événement doit être capté TRÈS TÔT — il est émis peu après le chargement —
// et `preventDefault()` doit être appelé de façon synchrone, sinon Chrome
// affiche sa propre invite en bas de l'écran. D'où l'initialisation depuis
// index.js, avant même le rendu de React.

import { safeLocalStorage, safeSessionStorage } from './safeStorage';

const CLE_REPORT = '1cours1bot.installation.reportee';
const CLE_VISITES = '1cours1bot.installation.visites';
const CLE_VISITE_COMPTEE = '1cours1bot.installation.visiteComptee';

// Une invitation refusée ne revient pas avant deux mois. Refuser doit être
// respecté longtemps, sans être définitif : quelqu'un qui découvre le service
// en septembre n'a pas le même avis en novembre.
const DELAI_AVANT_NOUVELLE_INVITATION_MS = 60 * 24 * 60 * 60 * 1000;

// On ne propose rien à la première visite. Un élève qui ouvre un lien partagé
// pour la première fois n'a pas encore de raison d'installer quoi que ce soit,
// et une invitation immédiate se lit comme une réclame.
const VISITES_AVANT_INVITATION = 2;

let evenementInstallation = null;
let dejaInstallee = false;
const abonnes = new Set();

function prevenirLesAbonnes() {
  abonnes.forEach((rappel) => {
    try {
      rappel();
    } catch (e) {
      /* un abonné qui lève ne doit pas empêcher les autres d'être prévenus */
    }
  });
}

/** L'application tourne-t-elle déjà en mode installé (sans barre d'adresse) ? */
export function estDejaInstallee() {
  if (dejaInstallee) return true;
  if (typeof window === 'undefined') return false;
  try {
    const modes = ['standalone', 'minimal-ui', 'fullscreen'];
    if (window.matchMedia && modes.some((m) => window.matchMedia(`(display-mode: ${m})`).matches)) {
      return true;
    }
    // Safari iOS n'implémente pas `display-mode` : il pose ce drapeau à la place.
    return window.navigator.standalone === true;
  } catch (e) {
    return false;
  }
}

/** iOS et iPadOS, tous navigateurs confondus (ils partagent tous WebKit). */
export function estSurIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  // iPadOS 13+ se présente comme un Mac. Le tactile fait la différence.
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
}

/**
 * L'appareil est-il un téléphone ou une tablette ?
 *
 * Le critère n'est PAS la largeur de la fenêtre : une fenêtre de bureau
 * rétrécie n'est pas un mobile, et un bandeau qui naîtrait d'un
 * redimensionnement se lirait comme un défaut. Ce qu'on interroge est le
 * pointeur PRINCIPAL : gros et sans survol, c'est un doigt. Un portable à
 * écran tactile garde sa souris comme pointeur principal — il reste un
 * ordinateur, et n'est donc pas invité.
 *
 * iOS répond avant la requête média : un iPhone et un iPad sont des mobiles
 * par construction, et un iPad muni d'un clavier à trackpad annonce
 * `hover: hover`, ce qui le ferait passer pour un ordinateur.
 */
export function estSurMobile() {
  if (estSurIos()) return true;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(pointer: coarse) and (hover: none)').matches;
  } catch (e) {
    return false;
  }
}

/**
 * L'application est-elle affichée dans une iframe ? Le chatbot public est
 * diffusable en iframe depuis un ENT : proposer d'installer une application
 * depuis l'intérieur d'un cadre n'aurait aucun sens, et l'installation y est
 * de toute façon refusée par le navigateur.
 */
export function dansUneIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    // Une exception de sécurité signifie précisément qu'on est encadré par un
    // document d'une autre origine.
    return true;
  }
}

/** Compte la visite en cours, une seule fois par session d'onglet. */
function compterLaVisite() {
  if (safeSessionStorage.getItem(CLE_VISITE_COMPTEE)) return;
  safeSessionStorage.setItem(CLE_VISITE_COMPTEE, '1');
  const precedent = parseInt(safeLocalStorage.getItem(CLE_VISITES) || '0', 10);
  const total = Number.isNaN(precedent) ? 1 : precedent + 1;
  safeLocalStorage.setItem(CLE_VISITES, String(total));
}

function nombreDeVisites() {
  const brut = parseInt(safeLocalStorage.getItem(CLE_VISITES) || '0', 10);
  return Number.isNaN(brut) ? 0 : brut;
}

function invitationReportee() {
  const brut = parseInt(safeLocalStorage.getItem(CLE_REPORT) || '0', 10);
  if (Number.isNaN(brut) || brut <= 0) return false;
  return Date.now() - brut < DELAI_AVANT_NOUVELLE_INVITATION_MS;
}

/** Repousse l'invitation. Sans stockage, le report ne vaut que pour la session. */
export function reporterInvitation() {
  safeLocalStorage.setItem(CLE_REPORT, String(Date.now()));
  prevenirLesAbonnes();
}

/**
 * Peut-on inviter à installer, et sous quelle forme ?
 * @returns {null|'bouton'|'consigne-ios'} `null` si on ne propose rien.
 */
export function formeDInvitation() {
  if (typeof window === 'undefined') return null;
  if (dansUneIframe()) return null;
  if (!estSurMobile()) return null;
  if (estDejaInstallee()) return null;
  if (nombreDeVisites() < VISITES_AVANT_INVITATION) return null;
  if (invitationReportee()) return null;
  if (evenementInstallation) return 'bouton';
  if (estSurIos()) return 'consigne-ios';
  return null;
}

/**
 * Déclenche l'installation. L'événement retenu n'est utilisable QU'UNE FOIS :
 * qu'elle soit acceptée ou refusée, on le jette.
 * @returns {Promise<boolean>} true si l'usager a accepté.
 */
export async function lancerInstallation() {
  const evenement = evenementInstallation;
  if (!evenement) return false;
  evenementInstallation = null;
  try {
    evenement.prompt();
    const choix = await evenement.userChoice;
    const accepte = choix && choix.outcome === 'accepted';
    if (!accepte) reporterInvitation();
    prevenirLesAbonnes();
    return !!accepte;
  } catch (e) {
    prevenirLesAbonnes();
    return false;
  }
}

/** S'abonner aux changements d'état. Rend la fonction de désabonnement. */
export function surChangement(rappel) {
  abonnes.add(rappel);
  return () => abonnes.delete(rappel);
}

/** À appeler une fois, au démarrage, avant le rendu de React. */
export function initialiserInstallationApp() {
  if (typeof window === 'undefined') return;

  compterLaVisite();

  window.addEventListener('beforeinstallprompt', (evenement) => {
    // Sans ce preventDefault, Chrome affiche sa propre invite : deux
    // propositions concurrentes pour la même chose.
    evenement.preventDefault();
    evenementInstallation = evenement;
    prevenirLesAbonnes();
  });

  window.addEventListener('appinstalled', () => {
    dejaInstallee = true;
    evenementInstallation = null;
    prevenirLesAbonnes();
  });
}
